import { Tooltip } from 'react-tooltip';
import Image from 'next/image';

export type AccoladeType =
  | 'quick_responder'
  | 'fast_shipper'
  | 'accurate_describer';

type AccoladeData = {
  title: string;
  description: string;
  icon: React.ReactNode;
};

const accoladeData: Record<AccoladeType, AccoladeData> = {
  quick_responder: {
    title: 'Quick Responder',
    description: 'Usually responds within 3 hours.',
    icon: (
      <Image
        src={'/icons/seller/quick_responder_accolade.png'}
        alt="Quick Reponder"
        height={64}
        width={64}
        className="h-16 w-auto"
      />
    ),
  },
  fast_shipper: {
    title: 'Fast Shipper',
    description: 'Usually ships in 1-3 business days.',
    icon: (
      <Image
        src={'/icons/seller/fast_shipper_accolade.png'}
        alt="Fast Shipper"
        height={64}
        width={64}
        className="h-16 w-auto"
      />
    ),
  },
  accurate_describer: {
    title: 'Accurate Describer',
    description: 'Items accurately match their descriptions.',
    icon: (
      <Image
        src={'/icons/seller/accurate_describer_accolade.png'}
        alt="Accurate Describer"
        height={64}
        width={64}
        className="h-16 w-auto"
      />
    ),
  },
};

type SellerAccoladeProps = {
  accolade: AccoladeType;
  count: number;
  isProfilePage?: boolean;
  title?: string;
  description?: string;
  iconOnly?: boolean;
};

export function SellerAccolade({
  accolade,
  count,
  isProfilePage = false,
  title,
  description,
  iconOnly = false,
}: SellerAccoladeProps) {
  const data = accoladeData[accolade];
  const displayTitle = title ?? data.title;
  const displayDescription = description ?? data.description;

  const threshold = isProfilePage ? 9 : 0;
  if (count <= threshold) return null;

  if (isProfilePage) {
    return (
      <div
        className="flex items-start gap-x-2 rounded-full px-8 py-2 sm:gap-[1rem] sm:rounded-none sm:px-0"
        data-tooltip-id={iconOnly ? 'accolade-tooltip' : undefined}
        data-tooltip-content={iconOnly ? displayTitle : undefined}
      >
        {data.icon}

        {!iconOnly && (
          <div className="flex flex-col items-start gap-1">
            <span className="text-[1.8rem] font-semibold">{displayTitle}</span>

            <p className="text-[1.5rem] font-medium text-zinc-400">
              {displayDescription}
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-start gap-x-4">
      <div className="h-[4rem] w-[4rem] flex-shrink-0">{data.icon}</div>
      <div className="flex flex-col gap-y-1">
        <span className="text-[1.6rem] font-semibold text-black">
          {displayTitle}
        </span>
        <p className="text-[1.3rem] text-[#444444]">{displayDescription}</p>
      </div>
    </div>
  );
}

export function AccoladeTooltip() {
  return <Tooltip id="accolade-tooltip" />;
}

export function getAccoladeIcon(accolade: AccoladeType): React.ReactNode {
  return accoladeData[accolade].icon;
}
